import {
  CardBody,
  CardHeader,
  Flex,
  Text,
  Card,
  Divider,
  Icon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  IconButton,
  Button,
  Tooltip,
  Tag,
  TagLabel,
} from '@chakra-ui/react';
import { useState, useEffect, useCallback } from 'react';
import { FaCheckCircle, FaTasks, FaTrash } from 'react-icons/fa';
import { fetchWithAuth } from '../../../utils/apiHelpers';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import ReskinnedTaskList from './ReskinnedTaskList';
import taskManagerService from '../tasks/ImprovedTaskManager/taskManager.service';
import { trackTaskStatusChanged } from '../../../utils/analytics/taskTracking';
// import { Tag } from 'rsuite';

function TaskList({ flags, onClick }) {
  const [tasks, setTasks] = useState([]);
  const [overflowY, setOverflowY] = useState('auto'); // Default overflow behavior

  const checkOverflow = useCallback(() => {
    const cardBody = document.getElementById('cardBody');
    // Add null check to prevent the error when cardBody is not found
    if (cardBody) {
      if (cardBody.scrollHeight > cardBody.clientHeight) {
        setOverflowY('scroll');
      } else {
        setOverflowY('auto');
      }
    }
  }, []);

  const fetchTasks = useCallback(() => {
    const taskImprovements = flags?.taskImprovements || false;

    if (taskImprovements) {
      // for improved task v2
      taskManagerService
        .getDashboardTasks()
        .then(data => {
          setTasks(data);
          checkOverflow(); // Check overflow after setting tasks
        })
        .catch(err => console.error(err));
    } else {
      // for older one
      const formDat = new FormData();
      formDat.append('type', 'assigned');
      fetchWithAuth('/api/get_tasks/', {
        method: 'POST',
        body: formDat,
      })
        .then(response => response.json())
        .then(data => {
          const tasksData = [];
          data['data'].forEach(item => {
            const temp = {
              id: item.id,
              ownerName: item.first_name,
              lastName: item.last_name,
              firstName: item.first_name,
              dueDate: item.due_on,
              header: item.task_title,
              status: item.status,
              description: item.task_desc,
              assignedToId: item.assigned_to_id,
            };
            tasksData.push(temp);
          });
          setTasks(tasksData);
          checkOverflow(); // Check overflow after setting tasks
        })
        .catch(err => console.error(err));
    }
  }, [flags?.taskImprovements, checkOverflow]);

  const modifyTasks = useCallback(
    (item, action, status) => {
      // Store the old status before making the change
      const oldStatus = item.status;

      const taskData = new FormData();

      taskData.append('taskId', item.id);
      taskData.append('action', action);
      taskData.append('status', status);

      fetchWithAuth('/api/modify_task/', {
        method: 'POST',
        body: taskData,
      })
        .then(response => response.json())
        .then(data => {
          // Track task status change in Heap
          trackTaskStatusChanged({
            taskId: item.id,
            taskTitle: item.task_title || item.header || '',
            oldStatus: oldStatus || '',
            newStatus: status,
          });

          fetchTasks();
        })
        .catch(err => console.error(err));
    },
    [fetchTasks]
  );

  const handleDelete = useCallback(
    id => {
      const taskList = [...tasks];
      const updatedTasks = taskList.filter(data => data.id !== id);
      setTasks(updatedTasks);
      checkOverflow(); // Check overflow after setting tasks
    },
    [tasks, checkOverflow]
  );

  // useEffect for componentDidMount equivalent
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // useEffect to refetch tasks when taskImprovements flag changes
  useEffect(() => {
    fetchTasks();
  }, [flags?.taskImprovements, fetchTasks]);

  // Check if the showReskin feature flag is enabled
  const showReskin = flags && flags.showReskin;

  // If showReskin is enabled, render the new reskinned task list
  if (showReskin) {
    return (
      <ReskinnedTaskList
        tasks={tasks}
        modifyTasks={modifyTasks}
        fetchTasks={fetchTasks}
        onClick={onClick}
      />
    );
  }

  // Otherwise, render the original task list
  return (
    <>
      <Card
        width={'100%'}
        whiteSpace={'wrap'}
        minHeight={'100%'}
        maxH={window.innerHeight * 0.5}
        p={1}
      >
        <CardHeader height={{ base: '70px', md: '70px', lg: '70px' }}>
          <Flex direction={'column'} height="100%">
            <Flex alignItems={'center'} gap={2}>
              <Icon as={FaTasks}></Icon>
              <Text fontSize={'md'}>Task List</Text>
            </Flex>
            <Text fontSize={'xs'}>Pending List</Text>
          </Flex>
        </CardHeader>
        <Divider mt={0} />
        <CardBody
          id="cardBody"
          p={2}
          pt={0}
          overflowY={overflowY}
          scrollBehavior={'smooth'}
        >
          <Accordion allowToggle fontSize={'sm'} fontWeight={'light'}>
            {tasks?.length ? (
              tasks.map(item => (
                <AccordionItem key={item.id} padding={0}>
                  <h2>
                    <Tooltip
                      hasArrow
                      label={item.header}
                      placement="bottom-start"
                    >
                      <AccordionButton height={'10'}>
                        <Flex width={'100%'}>
                          <Flex flex={2}>
                            <Text
                              padding={0}
                              textAlign="left"
                              fontSize={'xs'}
                              noOfLines={1}
                            >
                              {item.header}
                            </Text>
                          </Flex>
                          <Flex flex={1} alignItems={'center'}>
                            <Tag
                              color={'white'}
                              backgroundColor={
                                item.status === 'Completed'
                                  ? '#4db051'
                                  : item.status === 'Cancelled'
                                    ? '#fa8900'
                                    : item.status === 'In Progress'
                                      ? '#ffb303'
                                      : '#f54336'
                              }
                            >
                              <TagLabel fontSize={{ base: '10px', sm: '12px' }}>
                                {item.status}
                              </TagLabel>
                            </Tag>
                            {/* <Tag color={item.status === "Completed" ? "green" : item.status === "Cancelled" ? "orange" : item.status === "In Progress" ?"yellow":"red"}>{item.status}</Tag> */}
                          </Flex>
                        </Flex>
                        <AccordionIcon />
                      </AccordionButton>
                    </Tooltip>
                  </h2>
                  <AccordionPanel pb={4}>
                    <Flex direction={'column'}>
                      <pre
                        style={{
                          textWrap: 'wrap',
                        }}
                      >
                        {item.description}
                      </pre>
                      <Flex justifyContent={'center'} gap={3}>
                        <IconButton
                          as={Button}
                          icon={<FaCheckCircle />}
                          onClick={() => {
                            modifyTasks(item, 'status_update', 'Completed');
                          }}
                          title="Complete Task"
                        />
                        <IconButton
                          as={Button}
                          icon={<FaTasks />}
                          onClick={() => {
                            modifyTasks(item, 'status_update', 'In Progress');
                          }}
                          title="In Progress"
                        />
                        <IconButton
                          as={Button}
                          icon={<FaTrash />}
                          onClick={() => {
                            modifyTasks(item, 'status_update', 'Cancelled');
                          }}
                          title="Cancel Task"
                        />
                      </Flex>
                    </Flex>
                  </AccordionPanel>
                </AccordionItem>
              ))
            ) : (
              <></>
            )}
          </Accordion>
        </CardBody>
      </Card>
    </>
  );
}

export default withLDConsumer()(TaskList);
